import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  AlgorithmType,
  AlgorithmConfig,
  Athlete,
  College,
  Match,
} from "../types";

interface AppState {
  // UI State
  sidebarCollapsed: boolean;
  currentPage: string;
  theme: "light" | "dark";

  // Data State
  athletes: Athlete[];
  colleges: College[];
  matches: Match[];

  // Sync State
  lastSyncTime: Date | null;
  syncInProgress: boolean;

  // Matching State
  selectedAlgorithm: AlgorithmType;
  algorithmConfig: AlgorithmConfig;

  // Actions
  setSidebarCollapsed: (collapsed: boolean) => void;
  setCurrentPage: (page: string) => void;
  setTheme: (theme: "light" | "dark") => void;
  updateSyncStatus: (status: {
    lastSyncTime?: Date;
    syncInProgress: boolean;
  }) => void;
  setSelectedAlgorithm: (algorithm: AlgorithmType) => void;
  setAlgorithmConfig: (config: AlgorithmConfig) => void;
  setAthletes: (athletes: Athlete[]) => void;
  setColleges: (colleges: College[]) => void;
  setMatches: (matches: Match[]) => void;
}

// Force dark mode as default by clearing any stale light mode preference
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('sportsforce-admin-storage');
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      // Reset theme to dark if it was set to light
      if (parsed.state?.theme === 'light') {
        parsed.state.theme = 'dark';
        localStorage.setItem('sportsforce-admin-storage', JSON.stringify(parsed));
      }
    } catch {
      // If parsing fails, clear the storage
      localStorage.removeItem('sportsforce-admin-storage');
    }
  }
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Initial UI State
      sidebarCollapsed: false,
      currentPage: "dashboard",
      theme: "dark",

      // Initial Data State
      athletes: [],
      colleges: [],
      matches: [],

      // Initial Sync State
      lastSyncTime: null,
      syncInProgress: false,

      // Initial Matching State
      selectedAlgorithm: "topsis",
      algorithmConfig: {
        type: "topsis",
        weights: {
          academic: 35,
          athletic: 40,
          geographic: 15,
          financial: 10,
        },
      },

      // Actions
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      setCurrentPage: (page) => set({ currentPage: page }),

      setTheme: (theme) => set({ theme }),

      updateSyncStatus: (status) =>
        set((state) => ({
          lastSyncTime: status.lastSyncTime ?? state.lastSyncTime,
          syncInProgress: status.syncInProgress,
        })),

      setSelectedAlgorithm: (algorithm) =>
        set((state) => ({
          selectedAlgorithm: algorithm,
          algorithmConfig: {
            ...state.algorithmConfig,
            type: algorithm,
          },
        })),

      setAlgorithmConfig: (config) => set({ algorithmConfig: config }),

      setAthletes: (athletes) => set({ athletes }),

      setColleges: (colleges) => set({ colleges }),

      setMatches: (matches) => set({ matches }),
    }),
    {
      name: "sportsforce-admin-storage",
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        selectedAlgorithm: state.selectedAlgorithm,
        algorithmConfig: state.algorithmConfig,
      }),
    }
  )
);
